import { useState, useMemo, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { ITEMS, HABITATS, COLORS, shuffle, sample, animals } from "@/lib/kidsData";

const wordOf = (item, lang) => item.w[lang] || item.w.fr;

function Tile({ children, onClick, testid, state, big }) {
  const ring =
    state === "good" ? "border-emerald-400 bg-emerald-400/15" :
    state === "bad" ? "border-rose-400 bg-rose-400/15" :
    "border-white/15 bg-white/5 hover:border-primary/50 hover:bg-primary/10";
  return (
    <motion.button
      type="button"
      onClick={onClick}
      data-testid={testid}
      whileTap={{ scale: 0.94 }}
      animate={state === "bad" ? { x: [0, -8, 8, -6, 6, 0] } : {}}
      transition={{ duration: 0.4 }}
      className={`rounded-3xl border-2 flex items-center justify-center transition-colors ${ring} ${big ? "text-6xl sm:text-7xl w-full aspect-square" : "text-4xl sm:text-5xl w-full aspect-square"}`}
    >
      {children}
    </motion.button>
  );
}

/* 1 — Memory de paires */
export function MemoryGame({ age, onWin, speak, L }) {
  const deck = useMemo(() => {
    const picks = sample(ITEMS, age.pairs);
    return shuffle(picks.flatMap((it, i) => [
      { key: `${it.id}-a`, id: it.id, emoji: it.emoji },
      { key: `${it.id}-b`, id: it.id, emoji: it.emoji },
    ]));
  }, [age.pairs]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [busy, setBusy] = useState(false);

  const flip = (idx) => {
    if (busy || flipped.includes(idx) || matched.includes(deck[idx].id)) return;
    const next = [...flipped, idx];
    setFlipped(next);
    if (next.length === 2) {
      setBusy(true);
      const [a, b] = next;
      if (deck[a].id === deck[b].id) {
        setTimeout(() => {
          const nm = [...matched, deck[a].id];
          setMatched(nm); setFlipped([]); setBusy(false);
          if (nm.length === age.pairs) onWin();
        }, 500);
      } else {
        setTimeout(() => { setFlipped([]); setBusy(false); }, 850);
      }
    }
  };

  const cols = age.pairs <= 4 ? "grid-cols-3" : age.pairs <= 6 ? "grid-cols-4" : "grid-cols-4";
  return (
    <div data-testid="kids-memory">
      <p className="text-center text-foreground/60 mb-4">{L.memoryHint}</p>
      <div className={`grid ${cols} gap-3 max-w-xl mx-auto`}>
        {deck.map((c, i) => {
          const up = flipped.includes(i) || matched.includes(c.id);
          return (
            <Tile key={c.key} testid={`memory-card-${i}`} onClick={() => flip(i)} state={matched.includes(c.id) ? "good" : "idle"} big>
              {up ? <span>{c.emoji}</span> : <span className="text-primary/70 text-4xl">?</span>}
            </Tile>
          );
        })}
      </div>
    </div>
  );
}

/* Base pour Image↔mot et Qu'est-ce que c'est ? */
function PickWord({ age, lang, onWin, speak, L, blur, testidRoot }) {
  const [round, setRound] = useState(0);
  const [feedback, setFeedback] = useState(null); // {idx, state}
  const [revealed, setRevealed] = useState(!blur);

  const q = useMemo(() => {
    const target = sample(ITEMS, 1)[0];
    const distractors = sample(ITEMS.filter((it) => it.id !== target.id), age.options - 1);
    return { target, options: shuffle([target, ...distractors]) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round, age.options]);

  const pick = (opt, idx) => {
    if (feedback) return;
    if (opt.id === q.target.id) {
      setRevealed(true);
      setFeedback({ idx, state: "good" });
      speak && speak(L.sayFound(wordOf(q.target, lang)));
      setTimeout(() => {
        if (round + 1 >= age.rounds) { onWin(); }
        else { setRound((r) => r + 1); setFeedback(null); setRevealed(!blur); }
      }, 1100);
    } else {
      setFeedback({ idx, state: "bad" });
      setTimeout(() => setFeedback(null), 500);
    }
  };

  return (
    <div data-testid={testidRoot}>
      <p className="text-center text-foreground/60 mb-2">{blur ? L.whatHint : L.wordHint}</p>
      <p className="text-center text-primary/70 text-sm mb-5">{L.round} {round + 1}/{age.rounds}</p>
      <div className="flex justify-center mb-7">
        <div className={`text-8xl sm:text-9xl transition-all duration-500 ${revealed ? "blur-0 scale-100" : "blur-md scale-90"}`}>{q.target.emoji}</div>
      </div>
      <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
        {q.options.map((opt, i) => (
          <motion.button
            key={opt.id}
            type="button"
            onClick={() => pick(opt, i)}
            data-testid={`${testidRoot}-option-${i}`}
            whileTap={{ scale: 0.96 }}
            animate={feedback && feedback.idx === i && feedback.state === "bad" ? { x: [0, -8, 8, 0] } : {}}
            className={`rounded-2xl border-2 py-4 px-3 font-display text-xl transition-colors ${
              feedback && feedback.idx === i && feedback.state === "good" ? "border-emerald-400 bg-emerald-400/15 text-emerald-200" :
              feedback && feedback.idx === i && feedback.state === "bad" ? "border-rose-400 bg-rose-400/15" :
              "border-white/15 bg-white/5 hover:border-primary/50 hover:bg-primary/10 text-foreground/90"}`}
          >
            {wordOf(opt, lang)}
          </motion.button>
        ))}
      </div>
    </div>
  );
}

export function ImageWordGame(props) { return <PickWord {...props} blur={false} testidRoot="kids-imageword" />; }
export function WhatIsItGame(props) { return <PickWord {...props} blur={true} testidRoot="kids-whatisit" />; }

/* 3 — Trouve l'intrus */
export function IntrusGame({ age, lang, onWin, speak, L }) {
  const [round, setRound] = useState(0);
  const [feedback, setFeedback] = useState(null);

  const q = useMemo(() => {
    const cats = ["animal", "fruit", "vehicule", "objet"];
    const cat = sample(cats, 1)[0];
    const base = sample(ITEMS.filter((it) => it.cat === cat), 3);
    const intruder = sample(ITEMS.filter((it) => it.cat !== cat), 1)[0];
    return { tiles: shuffle([...base, intruder]), intruderId: intruder.id };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round]);

  const pick = (tile, idx) => {
    if (feedback) return;
    if (tile.id === q.intruderId) {
      setFeedback({ idx, state: "good" });
      setTimeout(() => {
        if (round + 1 >= age.rounds) onWin();
        else { setRound((r) => r + 1); setFeedback(null); }
      }, 900);
    } else {
      setFeedback({ idx, state: "bad" });
      setTimeout(() => setFeedback(null), 500);
    }
  };

  return (
    <div data-testid="kids-intrus">
      <p className="text-center text-foreground/60 mb-2">{L.intrusHint}</p>
      <p className="text-center text-primary/70 text-sm mb-5">{L.round} {round + 1}/{age.rounds}</p>
      <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
        {q.tiles.map((tile, i) => (
          <Tile key={tile.id} testid={`intrus-item-${i}`} onClick={() => pick(tile, i)}
                state={feedback && feedback.idx === i ? feedback.state : "idle"} big>
            {tile.emoji}
          </Tile>
        ))}
      </div>
    </div>
  );
}

/* 5 — Relie animal ↔ habitat */
export function RelieGame({ age, lang, onWin, speak, L }) {
  const picks = useMemo(() => sample(animals(), age.animals), [age.animals]);
  const habs = useMemo(() => shuffle(HABITATS), []);
  const [selected, setSelected] = useState(null);
  const [matched, setMatched] = useState([]);
  const [bad, setBad] = useState(null);

  const tapAnimal = (a) => { if (!matched.includes(a.id)) setSelected(a); };
  const tapHab = (h) => {
    if (!selected) return;
    if (selected.habitat === h.id) {
      const nm = [...matched, selected.id];
      setMatched(nm); setSelected(null);
      if (nm.length === picks.length) onWin();
    } else {
      setBad(h.id); setTimeout(() => setBad(null), 500);
    }
  };

  return (
    <div data-testid="kids-relie">
      <p className="text-center text-foreground/60 mb-5">{L.relieHint}</p>
      <div className="grid grid-cols-2 gap-6 max-w-xl mx-auto">
        <div className="space-y-3">
          {picks.map((a) => (
            <motion.button
              key={a.id} type="button" onClick={() => tapAnimal(a)}
              data-testid={`relie-animal-${a.id}`} whileTap={{ scale: 0.96 }}
              disabled={matched.includes(a.id)}
              className={`w-full rounded-2xl border-2 py-3 px-4 flex items-center gap-3 transition-colors ${
                matched.includes(a.id) ? "border-emerald-400 bg-emerald-400/15 opacity-60" :
                selected?.id === a.id ? "border-primary bg-primary/20" :
                "border-white/15 bg-white/5 hover:border-primary/50"}`}
            >
              <span className="text-4xl">{a.emoji}</span>
              <span className="font-display text-lg text-foreground/90">{wordOf(a, lang)}</span>
            </motion.button>
          ))}
        </div>
        <div className="space-y-3">
          {habs.map((h) => (
            <motion.button
              key={h.id} type="button" onClick={() => tapHab(h)}
              data-testid={`relie-habitat-${h.id}`} whileTap={{ scale: 0.96 }}
              animate={bad === h.id ? { x: [0, -8, 8, 0] } : {}}
              className="w-full rounded-2xl border-2 border-white/15 bg-white/5 hover:border-primary/50 py-3 px-4 flex items-center gap-3 transition-colors"
            >
              <span className="text-4xl">{h.emoji}</span>
              <span className="font-display text-lg text-foreground/90">{wordOf(h, lang)}</span>
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
}


const optsCols = (n) => (n === 3 ? "grid-cols-3" : "grid-cols-2");

/* 6 — Écoute et trouve (les sons de la nature) */
export function EcouteGame({ age, lang, onWin, speak, L }) {
  const [round, setRound] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const q = useMemo(() => {
    const target = sample(ITEMS, 1)[0];
    const distractors = sample(ITEMS.filter((it) => it.id !== target.id), age.options - 1);
    return { target, options: shuffle([target, ...distractors]) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round, age.options]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { speak && speak(L.sayFind(wordOf(q.target, lang))); }, [round]);

  const pick = (opt, idx) => {
    if (feedback) return;
    if (opt.id === q.target.id) {
      setFeedback({ idx, state: "good" });
      speak && speak(L.sayFound(wordOf(q.target, lang)));
      setTimeout(() => {
        if (round + 1 >= age.rounds) onWin();
        else { setRound((r) => r + 1); setFeedback(null); }
      }, 1000);
    } else {
      setFeedback({ idx, state: "bad" });
      setTimeout(() => setFeedback(null), 500);
    }
  };

  return (
    <div data-testid="kids-ecoute">
      <p className="text-center text-foreground/60 mb-2">{L.ecouteHint}</p>
      <p className="text-center text-primary/70 text-sm mb-5">{L.round} {round + 1}/{age.rounds}</p>
      <div className="flex justify-center mb-7">
        <button
          type="button" onClick={() => speak && speak(L.sayFind(wordOf(q.target, lang)))}
          data-testid="kids-ecoute-replay"
          className="inline-flex items-center gap-2 rounded-full border-2 border-primary/40 bg-primary/10 hover:bg-primary/20 text-primary px-6 py-4 font-display text-lg transition-colors"
        >
          <span className="text-3xl">🔊</span> {L.ecouteReplay}
        </button>
      </div>
      <div className={`grid ${optsCols(age.options)} gap-3 max-w-md mx-auto`}>
        {q.options.map((opt, i) => (
          <Tile key={opt.id} testid={`ecoute-item-${i}`} onClick={() => pick(opt, i)}
                state={feedback && feedback.idx === i ? feedback.state : "idle"} big>
            {opt.emoji}
          </Tile>
        ))}
      </div>
    </div>
  );
}

/* 7 — Compte avec moi */
export function CompteGame({ age, lang, onWin, speak, L }) {
  const maxN = age.pairs === 4 ? 5 : age.pairs === 6 ? 9 : 12;
  const [round, setRound] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const q = useMemo(() => {
    const item = sample(ITEMS, 1)[0];
    const n = 1 + Math.floor(Math.random() * maxN);
    const opts = new Set([n]);
    let guard = 0;
    while (opts.size < age.options && guard < 50) {
      guard++;
      const cand = n + (Math.floor(Math.random() * 5) - 2);
      if (cand >= 1 && cand <= maxN + 2) opts.add(cand);
    }
    return { item, n, options: shuffle([...opts]) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round, age.options]);

  const pick = (val, idx) => {
    if (feedback) return;
    if (val === q.n) {
      setFeedback({ idx, state: "good" });
      speak && speak(L.sayCount(q.n));
      setTimeout(() => {
        if (round + 1 >= age.rounds) onWin();
        else { setRound((r) => r + 1); setFeedback(null); }
      }, 1000);
    } else {
      setFeedback({ idx, state: "bad" });
      setTimeout(() => setFeedback(null), 500);
    }
  };

  return (
    <div data-testid="kids-compte">
      <p className="text-center text-foreground/60 mb-2">{L.compteHint}</p>
      <p className="text-center text-primary/70 text-sm mb-5">{L.round} {round + 1}/{age.rounds}</p>
      <div className="flex flex-wrap justify-center items-center gap-2 max-w-md mx-auto mb-8 min-h-[6rem]">
        {Array.from({ length: q.n }).map((_, i) => (
          <motion.span key={i} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: i * 0.08 }} className="text-5xl sm:text-6xl">
            {q.item.emoji}
          </motion.span>
        ))}
      </div>
      <div className={`grid ${optsCols(age.options)} gap-3 max-w-md mx-auto`}>
        {q.options.map((val, i) => (
          <motion.button
            key={val} type="button" onClick={() => pick(val, i)}
            data-testid={`compte-option-${i}`} whileTap={{ scale: 0.96 }}
            animate={feedback && feedback.idx === i && feedback.state === "bad" ? { x: [0, -8, 8, 0] } : {}}
            className={`rounded-2xl border-2 py-5 font-display text-4xl transition-colors ${
              feedback && feedback.idx === i && feedback.state === "good" ? "border-emerald-400 bg-emerald-400/15 text-emerald-200" :
              feedback && feedback.idx === i && feedback.state === "bad" ? "border-rose-400 bg-rose-400/15" :
              "border-white/15 bg-white/5 hover:border-primary/50 hover:bg-primary/10 text-foreground/90"}`}
          >
            {val}
          </motion.button>
        ))}
      </div>
    </div>
  );
}

/* 8 — La bonne couleur */
export function CouleurGame({ age, lang, onWin, speak, L }) {
  const colorName = (c) => c.w[lang] || c.w.fr;
  const [round, setRound] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const q = useMemo(() => {
    const target = sample(COLORS, 1)[0];
    const distractors = sample(COLORS.filter((c) => c.id !== target.id), age.options - 1);
    return { target, options: shuffle([target, ...distractors]) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round, age.options]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { speak && speak(L.sayFind(colorName(q.target))); }, [round]);

  const pick = (opt, idx) => {
    if (feedback) return;
    if (opt.id === q.target.id) {
      setFeedback({ idx, state: "good" });
      speak && speak(L.sayFound(colorName(q.target)));
      setTimeout(() => {
        if (round + 1 >= age.rounds) onWin();
        else { setRound((r) => r + 1); setFeedback(null); }
      }, 1000);
    } else {
      setFeedback({ idx, state: "bad" });
      setTimeout(() => setFeedback(null), 500);
    }
  };

  return (
    <div data-testid="kids-couleur">
      <p className="text-center text-foreground/60 mb-2">{L.couleurHint}</p>
      <p className="text-center text-primary/70 text-sm mb-5">{L.round} {round + 1}/{age.rounds}</p>
      <p className="text-center font-display text-4xl sm:text-5xl text-gradient-orange mb-8" data-testid="kids-couleur-target">{colorName(q.target)}</p>
      <div className={`grid ${optsCols(age.options)} gap-4 max-w-md mx-auto`}>
        {q.options.map((opt, i) => (
          <motion.button
            key={opt.id} type="button" onClick={() => pick(opt, i)}
            data-testid={`couleur-option-${i}`} whileTap={{ scale: 0.94 }}
            animate={feedback && feedback.idx === i && feedback.state === "bad" ? { x: [0, -8, 8, 0] } : {}}
            className={`rounded-3xl border-4 aspect-square w-full transition-all ${
              feedback && feedback.idx === i && feedback.state === "good" ? "border-emerald-300 ring-4 ring-emerald-300/40" :
              feedback && feedback.idx === i && feedback.state === "bad" ? "border-rose-300" : "border-white/20 hover:border-white/60"}`}
            style={{ backgroundColor: opt.hex }}
          />
        ))}
      </div>
    </div>
  );
}

/* 9 — La suite logique */
const SUITE_SYMBOLS = ["🔴", "🟡", "🔵", "🟢", "🟠", "🟣", "⭐", "❤️"];
export function SuiteGame({ age, onWin, speak, L }) {
  const [round, setRound] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const q = useMemo(() => {
    const syms = sample(SUITE_SYMBOLS, 3);
    const map = { A: syms[0], B: syms[1], C: syms[2] };
    const patterns = age.pairs === 4 ? ["ABAB"] : age.pairs === 6 ? ["ABAB", "AABB", "ABC"] : ["AABB", "ABC", "ABCABC", "ABB"];
    const unit = sample(patterns, 1)[0].split("").map((ch) => map[ch]);
    const showCount = age.pairs === 4 ? 4 : age.pairs === 6 ? 5 : 6;
    const full = [];
    let i = 0;
    while (full.length < showCount + 1) { full.push(unit[i % unit.length]); i++; }
    const answer = full[showCount];
    const shown = full.slice(0, showCount);
    const distract = sample(SUITE_SYMBOLS.filter((s) => s !== answer), age.options - 1);
    return { shown, answer, options: shuffle([answer, ...distract]) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round, age.options, age.pairs]);

  const pick = (opt, idx) => {
    if (feedback) return;
    if (opt === q.answer) {
      setFeedback({ idx, state: "good" });
      setTimeout(() => {
        if (round + 1 >= age.rounds) onWin();
        else { setRound((r) => r + 1); setFeedback(null); }
      }, 900);
    } else {
      setFeedback({ idx, state: "bad" });
      setTimeout(() => setFeedback(null), 500);
    }
  };

  return (
    <div data-testid="kids-suite">
      <p className="text-center text-foreground/60 mb-2">{L.suiteHint}</p>
      <p className="text-center text-primary/70 text-sm mb-5">{L.round} {round + 1}/{age.rounds}</p>
      <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-3 mb-8">
        {q.shown.map((s, i) => (
          <span key={i} className="text-4xl sm:text-5xl">{s}</span>
        ))}
        <span className="text-4xl sm:text-5xl w-14 h-14 sm:w-16 sm:h-16 rounded-2xl border-2 border-dashed border-primary/50 flex items-center justify-center text-primary/70">?</span>
      </div>
      <div className={`grid ${optsCols(age.options)} gap-3 max-w-md mx-auto`}>
        {q.options.map((opt, i) => (
          <Tile key={i} testid={`suite-option-${i}`} onClick={() => pick(opt, i)}
                state={feedback && feedback.idx === i ? feedback.state : "idle"} big>
            {opt}
          </Tile>
        ))}
      </div>
    </div>
  );
}

/* 10 — L'ombre mystère */
export function OmbreGame({ age, lang, onWin, speak, L }) {
  const [round, setRound] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const q = useMemo(() => {
    const target = sample(ITEMS, 1)[0];
    const distractors = sample(ITEMS.filter((it) => it.id !== target.id), age.options - 1);
    return { target, options: shuffle([target, ...distractors]) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round, age.options]);

  const pick = (opt, idx) => {
    if (feedback) return;
    if (opt.id === q.target.id) {
      setRevealed(true);
      setFeedback({ idx, state: "good" });
      speak && speak(L.sayFound(wordOf(q.target, lang)));
      setTimeout(() => {
        if (round + 1 >= age.rounds) onWin();
        else { setRound((r) => r + 1); setFeedback(null); setRevealed(false); }
      }, 1100);
    } else {
      setFeedback({ idx, state: "bad" });
      setTimeout(() => setFeedback(null), 500);
    }
  };

  return (
    <div data-testid="kids-ombre">
      <p className="text-center text-foreground/60 mb-2">{L.ombreHint}</p>
      <p className="text-center text-primary/70 text-sm mb-5">{L.round} {round + 1}/{age.rounds}</p>
      <div className="flex justify-center mb-8">
        <div
          className="text-8xl sm:text-9xl transition-all duration-500"
          style={{ filter: revealed ? "none" : "brightness(0)", opacity: revealed ? 1 : 0.9 }}
          data-testid="kids-ombre-shadow"
        >
          {q.target.emoji}
        </div>
      </div>
      <div className={`grid ${optsCols(age.options)} gap-3 max-w-md mx-auto`}>
        {q.options.map((opt, i) => (
          <Tile key={opt.id} testid={`ombre-item-${i}`} onClick={() => pick(opt, i)}
                state={feedback && feedback.idx === i ? feedback.state : "idle"} big>
            {opt.emoji}
          </Tile>
        ))}
      </div>
    </div>
  );
}
